import React, {
 useState, useContext, createContext, useRef, useEffect,
} from 'react';
import PropTypes from 'prop-types';
import { post } from '../../../utils/api/base';
import env from '../../../Env';
import { SESSION_TIMEOUT_MS, SESSION_CHECK_INTERVAL_MS } from '../../../config/sessionConfig';

const authContext = createContext();

// Hook for child components to get the auth object ...
// ... and re-render when it changes.
export const useAuth = () => useContext(authContext);

const checkIsAuthenticated = () => {
  const key = 'isAuthenticated';
  const itemStr = localStorage.getItem(key);

  if (!itemStr) {
    return false;
  }

  const item = JSON.parse(itemStr);

  if (new Date().getTime() > item.expiry) {
    // Clear client-side authentication first
    localStorage.removeItem(key);
    return false;
  }

  const value = item.value === 'true';
  
  return value;
};

// Provider hook that creates auth object and handles state
function useProvideAuth() {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setAuthenticated] = useState(checkIsAuthenticated());
  const sessionIntervalRef = useRef(null);

  const signout = () => {
    // Clear the session check interval if it exists
    if (sessionIntervalRef.current) {
      clearInterval(sessionIntervalRef.current);
      sessionIntervalRef.current = null;
    }
    localStorage.removeItem('isAuthenticated');
    setAuthenticated(false);

    window.location.href = '/login';
  };

  const checkSessionExpiry = async () => {
    const itemStr = localStorage.getItem('isAuthenticated');
    if (!itemStr) {
      return;
    }

    const authStoredItem = JSON.parse(itemStr);
    const now = new Date().getTime();
    if (now > authStoredItem.expiry) {
      // Session expired - clear server-side tokens and sign out
      try {
        await post(`${env.baseApiUrl}/api/authentication/logout`, {});
      } catch (error) {
        console.error('Logout API error during session expiry:', error);
      }
      // Clear interval manually (don't call signout() to avoid triggering PrivateRoute redirect
      // from setAuth(false) while we are inside an interval callback as that results in errors!)
      if (sessionIntervalRef.current) {
        clearInterval(sessionIntervalRef.current);
        sessionIntervalRef.current = null;
      }
      localStorage.removeItem('isAuthenticated');
      // Use window.location for navigation as inside interval callback so easier re scope than using history!
      window.location.href = '/log_in';
    }
  };

  const signin = () => {
    const item = {
      value: 'true',
      // this expiry is reset each time the user calls the api (in axios.js)
      // and is monitored every minute with forced logout at expiry time
      expiry: new Date().getTime() + SESSION_TIMEOUT_MS,
    };
    localStorage.setItem('isAuthenticated', JSON.stringify(item));

    // Check session expiry every minute and force logout if no server activity for 15 mins
    // NOTE: this is a (hopefully) temp fix to force session logout per pen test finding and LLX requirements
    // Ideally we will redevelop admin app and have auth that better meets the requirement to have short sessions 
    // (rather than using jwts!)
    // Clear any existing interval before creating a new one
    if (sessionIntervalRef.current) {
      clearInterval(sessionIntervalRef.current);
    }
    // Check immediately when user signs in
    checkSessionExpiry();
    // Then check at regular intervals
    sessionIntervalRef.current = setInterval(checkSessionExpiry, SESSION_CHECK_INTERVAL_MS);
    setAuthenticated(true);
  };

  // Handle page visibility changes and the throttling of interval for session timeout
  // i.e. make sure we check the status immediately on getting focus back
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated) {
        checkSessionExpiry();
      }
    };
    const handleFocus = () => {
      if (isAuthenticated) {
        checkSessionExpiry();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated]); 

  // Return the user object and auth methods
  return {
    user,
    signin,
    signout,
    isAuthenticated,
  };
}

// Provider component that wraps your app and makes auth object ...
// ... available to any child component that calls useAuth().
export function ProvideAuth({ children }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

ProvideAuth.propTypes = {
  children: PropTypes.shape().isRequired,
};
